/**
 * Strikes (Foundry: CharacterPF2e#prepareAttacks / #prepareStrike and system/damage/weapon.ts).
 * Every weapon the Character can attack with becomes a strike: the attack modifier from
 * proficiency, attribute and item bonus with every synthetic modifier of its domains; the
 * Multiple Attack Penalty variants; and the damage formula for a success and a critical success.
 * No dice are rolled here (#19); everything a Roll consumes is on the strike.
 */
import { abpLevelOf } from "./inventory";
import { applyDamageAlteration, createDamageDice, damageDiceRollOptions, testDamageDice, type DamageDice } from "./damage-dice";
import { createDamageFormula, type BaseDamageData, type Degree, type FormulaLabels } from "./damage-formula";
import { damageTypeLabel, localize, weaponTraitLabel } from "./labels";
import { applyAdjustments, createAttributeModifier, createModifier, createProficiencyModifier, resolveModifiers, signed, type Modifier } from "./modifiers";
import { testBeforeRoll, testPredicate } from "./predicate";
import { weaponProficiencyRank } from "./proficiencies";
import { evaluateFormula, replaceFormulaData } from "./formula";
import { getProperty } from "./actor-data";
import { ammoMatches, shieldWeapon } from "./inventory";
import { WEAPON_PROPERTY_RUNES, type WeaponPropertyRuneData } from "./runes-data";
import { applyItemAlterationsTo } from "./rule-elements/item-alteration";
import type { EngineState } from "./state";
import { collectCheckExtras, type CheckEphemeralEffect, type CheckExtras, type CheckNote } from "./statistics";
import { extractDamageAlterations, extractDamageDice, extractEphemeralEffects, extractModifierAdjustments, extractModifiers, extractNotes, processDamageCategoryStacking, type CritSpecEffect, type DegreeAdjustment, type RollNote } from "./synthetics";
import { EQUIVALENT_WEAPONS, MATERIAL_DAMAGE_EFFECTS, PROFICIENCY_RANKS, PROFICIENCY_RANK_OPTION, isDamageDieSize, type Attribute, type DamageDieSize, type Rank } from "./values";
import { EngineWeapon, weaponEntry, type WeaponDamageData, type WeaponRunes } from "./weapons";

export interface StrikeVariant {
  /** The Multiple Attack Penalty applied: 0, map1 or map2. */
  penalty: number;
  total: number;
  label: string;
}

export interface DerivedStrikeDamage {
  domains: string[];
  options: Set<string>;
  base: BaseDamageData[];
  dice: DamageDice[];
  modifiers: Modifier[];
  materials: string[];
  formula: Record<Degree, string | null>;
  breakdown: Record<Degree, string[]>;
  notes: CheckNote[];
  /** Effects applied for the damage roll (an EphemeralEffect on a damage selector). */
  ephemeralEffects: CheckEphemeralEffect[];
  /** Whether a critical specialization effect applies. */
  criticalSpecialization: boolean;
  /** Resistances the damage ignores, from property runes (#19 applies them); Infinity means all of it. */
  ignoredResistances: { type: string; max: number }[];
}

/** The ammunition a strike draws on (#18 fires it): what the weapon takes and the compatible stacks held. */
export interface StrikeAmmunition {
  baseType: string | null;
  /** The inventory id of the selected stack, when one is. */
  selected: string | null;
  compatible: { id: string; name: string; quantity: number }[];
}

export interface DerivedStrike {
  slug: string;
  label: string;
  weapon: EngineWeapon;
  /** The Entry the strike comes from: the weapon Entry, or the Entry whose Strike Rule Element made it; null for the basic unarmed attack. */
  source: string | null;
  sourceName: string | null;
  proficiencyRank: Rank;
  domains: string[];
  /** The roll options the attack modifier resolved with. */
  options: Set<string>;
  /** Options set only when the strike is rolled (`action:strike`, proficiency, traits). */
  baseOptions: string[];
  modifiers: Modifier[];
  totalModifier: number;
  breakdown: string;
  map: { slug: string; label: string; map1: number; map2: number };
  variants: [StrikeVariant, StrikeVariant, StrikeVariant];
  ready: boolean;
  visible: boolean;
  handsAvailable: boolean;
  canAttack: boolean;
  actionTraits: string[];
  weaponTraits: string[];
  extras: CheckExtras;
  damage: DerivedStrikeDamage | null;
  versatileOptions: { value: string; selected: boolean; label: string }[];
  altUsages: DerivedStrike[];
  battleForm: boolean;
  /** The Override key for the attack modifier. */
  overrideKey: string;
  /** Null for a weapon that needs no ammunition. */
  ammunition: StrikeAmmunition | null;
}

/** Foundry's getStrikeAttackDomains, with the best attribute chosen among the default, finesse/brutal and ability synthetics. */
export function getStrikeAttackDomains(state: EngineState, weapon: EngineWeapon, proficiencyRank: Rank | null, baseRollOptions: string[]): string[] {
  const unarmedOrWeapon = weapon.category === "unarmed" ? "unarmed" : "weapon";
  const meleeOrRanged = weapon.isMelee ? "melee" : "ranged";
  const weaponSlug = weapon.slug;
  const domains = [
    ...(weapon.baseType ? [`${weapon.baseType}-base-attack-roll`] : []),
    ...(weapon.group ? [`${weapon.group}-group-attack-roll`] : []),
    ...weapon.otherTags.map((t) => `${t}-tag-attack-roll`),
    `${weapon.id}-attack`,
    `${weaponSlug}-attack`,
    `${weaponSlug}-attack-roll`,
    `${unarmedOrWeapon}-attack-roll`,
    `${meleeOrRanged}-attack-roll`,
    `${meleeOrRanged}-strike-attack-roll`,
    "strike-attack-roll",
    "attack-roll",
    "attack",
    "check",
    "all",
  ];
  if (typeof proficiencyRank === "number") domains.push(`${PROFICIENCY_RANKS[proficiencyRank]}-attack`);
  const abilities = state.actor.system.abilities;
  const defaultAttributeModifier = createAttributeModifier(weapon.defaultAttribute, abilities[weapon.defaultAttribute].mod, domains);
  const rollOptions = new Set([...baseRollOptions, ...state.rollOptions.get(domains), ...weapon.getRollOptions("item")]);
  const traits = new Set(weapon.traits);
  const alternative = traits.has("finesse") ? createAttributeModifier("dex", abilities.dex.mod, domains) : traits.has("brutal") ? createAttributeModifier("str", abilities.str.mod, domains) : null;
  const candidates = [defaultAttributeModifier, alternative, ...extractModifiers(state.synthetics, domains, { resolvables: { weapon }, injectables: { weapon }, test: rollOptions })]
    .filter((m): m is Modifier & { ability: Attribute } => !!m && m.type === "ability" && m.enabled && !!m.ability);
  const best = candidates.reduce((a, b) => (b.value > a.value ? b : a));
  domains.push(`${best.ability}-attack`, `${best.ability}-based`);
  return [...new Set(domains)];
}

/** Foundry's getAttackDamageDomains. */
export function getAttackDamageDomains(state: EngineState, weapon: EngineWeapon, proficiencyRank: Rank | null): string[] {
  const meleeOrRanged = weapon.isMelee ? "melee" : "ranged";
  const slug = weapon.slug;
  const traits = weapon.traits;
  const baseType = EQUIVALENT_WEAPONS[weapon.baseType ?? ""] ?? weapon.baseType;
  const unarmedOrWeapon = traits.includes("unarmed") ? "unarmed" : "weapon";
  const domains = [
    `${weapon.id}-damage`,
    `${weapon.id}-${meleeOrRanged}-damage`,
    `${slug}-damage`,
    `${meleeOrRanged}-strike-damage`,
    `${meleeOrRanged}-damage`,
    `${unarmedOrWeapon}-damage`,
    ...(weapon.group ? [`${weapon.group}-weapon-group-damage`] : []),
    ...(baseType ? [`${baseType}-base-damage`] : []),
    "attack-damage",
    "strike-damage",
    "damage",
  ];
  if (weapon.baseType) domains.push(`${weapon.baseType}-base-type-damage`);
  if (typeof proficiencyRank === "number") domains.push(`${PROFICIENCY_RANKS[proficiencyRank]}-damage`);
  if (baseType && !domains.includes(`${baseType}-damage`)) domains.push(`${baseType}-damage`);
  const strengthBased = weapon.isMelee || traits.includes("propulsive") || (weapon.isThrown && !traits.includes("splash") && !["alchemical-bomb", "grenade"].includes(baseType ?? ""));
  const candidates = [
    strengthBased ? createAttributeModifier("str", state.actor.system.abilities.str.mod, domains) : null,
    ...extractModifiers(state.synthetics, domains, { resolvables: { weapon }, injectables: { weapon }, test: new Set([...state.rollOptions.get(domains), ...weapon.getRollOptions("item")]) }).filter((m) => !m.ignored && m.type === "ability"),
  ].filter((m): m is Modifier => !!m);
  const best = candidates.reduce((a: Modifier | null, b) => (a && b.value <= a.value ? a : b), null);
  if (best?.ability) domains.push(`${best.ability}-damage`);
  return [...new Set(domains)];
}

interface MAPData {
  slug: "multiple-attack-penalty";
  label: string;
  map1: number;
  map2: number;
}

/** Foundry's calculateMAPs: the lowest penalty among the base (agile or not) and the MultipleAttackPenalty synthetics. */
export function calculateMAPs(state: EngineState, weapon: EngineWeapon, domains: string[], options: Set<string>): MAPData {
  const label = localize(state, "PF2E.MultipleAttackPenalty", "Multiple Attack Penalty");
  const base: MAPData = weapon.traits.includes("agile") ? { slug: "multiple-attack-penalty", label, map1: -4, map2: -8 } : { slug: "multiple-attack-penalty", label, map1: -5, map2: -10 };
  const fromSynthetics = domains
    .flatMap((d) => state.synthetics.multipleAttackPenalties[d] ?? [])
    .filter((p) => testPredicate(p.predicate, new Set([...options, ...p.parentOptions])))
    .map((p): MAPData => ({ slug: "multiple-attack-penalty", label: p.label, map1: p.penalty, map2: p.penalty * 2 }));
  return [base, ...fromSynthetics].reduce((lowest, p) => (p.map1 > lowest.map1 ? p : lowest));
}


/** Foundry's AttackTraitHelpers and PCAttackTraitHelpers: modifiers weapon traits and tags add to attack rolls. */
function createAttackTraitModifiers(state: EngineState, weapon: EngineWeapon, domains: string[]): Modifier[] {
  const adjustments = (slug: string) => extractModifierAdjustments(state.synthetics.modifierAdjustments, domains, slug);
  return [...weapon.traits, ...weapon.otherTags].flatMap((trait): Modifier[] => {
    const unannotated = trait.replace(/-d?\d{1,3}$/, "");
    switch (unannotated) {
      case "volley": {
        const rangeIncrement = weapon.range?.increment;
        if (!rangeIncrement) return [];
        const penaltyRange = Number(/-(\d+)$/.exec(trait)?.[1]);
        return [createModifier({ slug: unannotated, label: weaponTraitLabel(state, trait), value: -2, type: "untyped", ignored: true, predicate: [{ lte: ["target:distance", penaltyRange] }, { not: "self:ignore-volley-penalty" }], adjustments: adjustments(unannotated) })];
      }
      case "sweep":
        return [createModifier({ slug: unannotated, label: weaponTraitLabel(state, trait), value: 1, type: "circumstance", predicate: ["sweep-bonus"] })];
      case "backswing":
        return [createModifier({ slug: unannotated, label: weaponTraitLabel(state, trait), value: 1, type: "circumstance", predicate: ["backswing-bonus"] })];
      case "kickback":
        return [createModifier({ slug: unannotated, label: weaponTraitLabel(state, trait), value: -2, type: "circumstance", predicate: [{ lt: ["attribute:str:mod", 2] }], adjustments: adjustments(unannotated) })];
      case "improvised":
        return [createModifier({ slug: unannotated, label: localize(state, "PF2E.Item.Physical.OtherTag.Improvised", "Improvised"), value: -2, type: "item", predicate: [{ not: "self:ignore-improvised-penalty" }], adjustments: adjustments(unannotated) })];
      default:
        return [];
    }
  });
}

export interface PrepareStrikesOptions {
  includeBasicUnarmed?: boolean;
}

/** Build every weapon the Character strikes with, in Foundry's order, and prepare a strike for each. */
export function prepareStrikes(state: EngineState, options: PrepareStrikesOptions = {}): DerivedStrike[] {
  const includeBasicUnarmed = options.includeBasicUnarmed ?? true;
  // The inventory's weapons had their ItemAlterations applied with every other held Entry in the applyAEs phase.
  const carried = state.inventory.items.filter((i): i is EngineWeapon => i instanceof EngineWeapon);

  const handwraps = carried.find((w) => w.otherTags.includes("handwraps-of-mighty-blows") && w.category === "unarmed" && w.isEquipped && w.isInvested !== false);
  const unarmedRunes: WeaponRunes = handwraps ? { potency: handwraps.runes.potency, striking: handwraps.runes.striking, property: [...handwraps.runes.property] } : { potency: 0, striking: 0, property: [] };

  const basicUnarmed = includeBasicUnarmed ? createBasicUnarmed(state, unarmedRunes) : null;
  if (basicUnarmed) applyItemAlterationsTo(state, basicUnarmed);
  const synthetic = Object.values(state.synthetics.strikes).map((s) => s(unarmedRunes)).filter((w): w is EngineWeapon => !!w);
  // Every shield the Character holds or wears can be bashed with (Foundry: ShieldPF2e#generateWeapon).
  const shields = state.inventory.items.filter((i) => i.type === "shield").map((shield) => shieldWeapon(state, shield)).filter((w): w is EngineWeapon => !!w);
  for (const weapon of shields) applyItemAlterationsTo(state, weapon);
  const weapons = [...carried.filter((w) => !w.otherTags.includes("handwraps-of-mighty-blows")), ...synthetic, ...(basicUnarmed ? [basicUnarmed] : []), ...shields];

  const handsReallyFree = state.handsReallyFree;
  const strikes = weapons
    .map((w) => prepareStrike(state, w, handsReallyFree))
    .sort((a, b) => a.label.toLowerCase().replace(/[-0-9\s]/g, "").localeCompare(b.label.toLowerCase().replace(/[-0-9\s]/g, ""), "en"))
    .sort((a, b) => (a.slug === "basic-unarmed" ? 1 : b.slug === "basic-unarmed" ? -1 : 0))
    .sort((a, b) => (a.weapon.isHeld === b.weapon.isHeld ? 0 : a.weapon.isHeld ? -1 : 1))
    .sort((a, b) => (a.ready === b.ready ? 0 : a.ready ? -1 : 1));
  for (const strike of strikes) {
    strike.altUsages.push(...strike.weapon.getAltUsages().map((w) => prepareStrike(state, w, handsReallyFree)));
  }
  return strikes;
}

function createBasicUnarmed(state: EngineState, runes: WeaponRunes): EngineWeapon {
  const entry = weaponEntry("xxPF2ExUNARMEDxx", localize(state, "PF2E.WeaponTypeUnarmed", "Unarmed Attack"), "basic-unarmed", {
    slug: "basic-unarmed",
    category: "unarmed",
    baseItem: null,
    bonus: { value: 0 },
    damage: { dice: 1, die: "d4", damageType: "bludgeoning" },
    equipped: { carryType: "worn", inSlot: true, handsHeld: 0 },
    group: "brawling",
    traits: { value: ["agile", "finesse", "nonlethal", "unarmed"], otherTags: [], rarity: "common" },
    usage: { value: "worngloves" },
    runes: { potency: runes.potency, striking: runes.striking, property: [...runes.property] },
  });
  return new EngineWeapon({ entry, carryType: "worn", handsHeld: 0, abpLevel: abpLevelOf(state) });
}

/** Foundry's CharacterPF2e#prepareStrike. */
export function prepareStrike(state: EngineState, weapon: EngineWeapon, handsReallyFree: number): DerivedStrike {
  const synthetics = state.synthetics;
  const modifiers: Modifier[] = [];

  const runes = propertyRunesOf(weapon);
  for (const adjustment of synthetics.strikeAdjustments) adjustment.adjustWeapon?.(weapon);
  // Property runes that change the weapon (Foundry: getPropertyRuneStrikeAdjustments): hooked adds trip, merciful nonlethal, impossible doubles the range.
  for (const rune of runes) {
    for (const trait of rune.addWeaponTraits ?? []) if (!weapon.system.traits!.value!.includes(trait)) weapon.system.traits!.value!.push(trait);
    if (rune.doubleRange && typeof weapon.system.range === "number" && typeof weapon.entry.system.range === "number") {
      const sourceRange = weapon.entry.system.range;
      weapon.system.range = sourceRange * 2 + Math.abs(weapon.system.range - sourceRange);
    }
  }
  weapon.adjustForGrip();
  weapon.processTwoHandTrait();

  const weaponTraits = weapon.traits;
  const weaponRollOptions = new Set(weapon.getRollOptions("item"));
  const proficiencyRank = weaponProficiencyRank(state.actor.system.proficiencies.attacks, { category: weapon.category, group: weapon.group, baseItem: weapon.baseType }, weaponRollOptions);
  const meleeOrRanged = weapon.isMelee ? "melee" : "ranged";
  const baseOptions = ["action:strike", "self:action:slug:strike", `item:proficiency:rank:${proficiencyRank}`, PROFICIENCY_RANK_OPTION[proficiencyRank], ...weaponTraits, meleeOrRanged];
  const attackDomains = getStrikeAttackDomains(state, weapon, proficiencyRank, baseOptions);
  const adjustments = (slug: string) => extractModifierAdjustments(synthetics.modifierAdjustments, attackDomains, slug);
  const abilities = state.actor.system.abilities;

  const defaultAttribute = weapon.defaultAttribute;
  modifiers.push(createAttributeModifier(defaultAttribute, abilities[defaultAttribute].mod, attackDomains, undefined, adjustments(defaultAttribute)));
  if (weapon.isMelee && weaponTraits.includes("finesse")) modifiers.push(createAttributeModifier("dex", abilities.dex.mod, attackDomains, undefined, adjustments("dex")));
  if (weapon.isRanged && weaponTraits.includes("brutal")) modifiers.push(createAttributeModifier("str", abilities.str.mod, attackDomains, undefined, adjustments("str")));
  modifiers.push(createProficiencyModifier({ rank: proficiencyRank, level: state.level, withoutLevel: state.variantRules.proficiencyWithoutLevel, adjustments: adjustments("proficiency") }));

  const initialRollOptions = new Set([...baseOptions, ...state.rollOptions.get(attackDomains), ...weaponRollOptions]);

  if (weapon.group === "bomb" && !state.variantRules.automaticBonusProgression) {
    const attackBonus = Number(weapon.system.bonus?.value) || 0;
    if (attackBonus !== 0) modifiers.push(createModifier({ label: localize(state, "PF2E.ItemBonusLabel", "Item Bonus"), value: attackBonus, type: "item" }));
  }

  // Item or potency bonus to attack rolls: the potency rune against any WeaponPotency synthetic (ABP, #12).
  {
    const potencyRune = weapon.runes.potency;
    const potencySynthetics = attackDomains.flatMap((key) => synthetics.weaponPotency[key] ?? []).filter((wp) => testPredicate(wp.predicate, initialRollOptions));
    const bestSynthetic = potencySynthetics.length > 0 ? potencySynthetics.reduce((highest, current) => (highest.bonus > current.bonus ? highest : current)) : null;
    type BonusSource = { slug: string; label: string; type: "item" | "potency"; value: number; magical?: boolean };
    const sources: BonusSource[] = [{ slug: "weapon-potency", label: localize(state, "PF2E.Item.Weapon.Rune.Potency", "Weapon Potency"), type: "item", value: potencyRune }];
    if (bestSynthetic) sources.push({ slug: bestSynthetic.type === "item" ? "weapon-potency" : "attack-potency", label: bestSynthetic.label, type: bestSynthetic.type, value: bestSynthetic.bonus, magical: true });
    const best = sources.reduce((result, current) => (result.value >= current.value ? result : current));
    if (best.value > 0) {
      modifiers.push(createModifier({ slug: best.slug, label: best.label, type: best.type, value: best.value, adjustments: adjustments(best.slug) }));
      if (!weapon.isMagical && best.magical && (best.type === "item" || !state.variantRules.automaticBonusProgression)) weapon.system.traits!.value!.push("magical");
      weapon.attackItemBonus = best.value;
    }
  }

  if (weapon.otherTags.includes("shoddy")) modifiers.push(createModifier({ slug: "shoddy", label: localize(state, "PF2E.Item.Physical.OtherTag.Shoddy", "Shoddy"), type: "item", value: -2, adjustments: adjustments("shoddy") }));
  modifiers.push(...createAttackTraitModifiers(state, weapon, attackDomains), ...extractModifiers(synthetics, attackDomains, { injectables: { weapon }, resolvables: { weapon } }));

  const rollOptions = new Set([...state.rollOptions.get(attackDomains), ...weaponRollOptions, ...weaponTraits, meleeOrRanged]);
  const resolved = resolveModifiers(modifiers, rollOptions);

  const handsAvailable = !weapon.graspingAppendage || handsReallyFree > 0;
  const actionTraits: string[] = ["attack"];
  for (const adjustment of synthetics.strikeAdjustments) adjustment.adjustTraits?.(weapon, actionTraits);
  for (const rune of runes) for (const trait of rune.addActionTraits ?? []) if (!actionTraits.includes(trait)) actionTraits.push(trait);
  const ready = (weapon.isEquipped && handsAvailable) || (weapon.isThrown && weapon.reload === "0" && weapon.isWorn && handsReallyFree > 0);
  const hiddenCauseStowed = weapon.isStowed;
  const hiddenCauseUnarmed = weapon.slug === "basic-unarmed" && state.actor.flags.pf2e.showBasicUnarmed === false;

  const versatileOptions = weapon.altUsageType === "thrown" ? [] : weapon.versatileOptions.map((o) => ({ value: o, selected: weapon.traitToggles.versatile === o, label: damageTypeLabel(state, o) }));
  if (versatileOptions.length > 0) versatileOptions.unshift({ value: weapon.damage.damageType, selected: weapon.traitToggles.versatile === null, label: damageTypeLabel(state, weapon.damage.damageType) });

  const maps = calculateMAPs(state, weapon, attackDomains, initialRollOptions);
  const mapLabel = localize(state, "PF2E.MAPAbbreviationValueLabel", "{value} (MAP {penalty})");
  const variants = ([0, 1, 2] as const).map((increases): StrikeVariant => {
    if (increases === 0) return { penalty: 0, total: resolved.total, label: signed(resolved.total) };
    const penalty = createModifier({ slug: maps.slug, label: maps.label, value: maps[`map${increases}`], adjustments: adjustments(maps.slug) });
    applyAdjustments(penalty, initialRollOptions);
    const value = penalty.ignored ? 0 : penalty.value;
    return { penalty: value, total: resolved.total + value, label: value ? mapLabel.replace("{value}", signed(resolved.total + value)).replace("{penalty}", String(value)) : signed(resolved.total) };
  }) as [StrikeVariant, StrikeVariant, StrikeVariant];

  const extras = collectCheckExtras(state, attackDomains, initialRollOptions);
  // Property runes' attack notes and degree-of-success adjustments (keen, hopeful, returning) join what a Roll consumes.
  const testNote = (predicate: RollNote["predicate"]): { enabled: boolean; postRoll: boolean } => {
    if (predicate.length === 0) return { enabled: true, postRoll: false };
    const r = testBeforeRoll(predicate, initialRollOptions);
    return { enabled: r.passes, postRoll: r.postRoll };
  };
  for (const rune of runes) {
    for (const note of rune.attack?.notes ?? []) extras.notes.push({ ...runeNote(state, weapon, rune, note, "attack-roll"), ...testNote(note.predicate ?? []) });
    for (const dos of rune.attack?.dosAdjustments ?? []) {
      extras.degreeAdjustments.push({ adjustments: Object.fromEntries(Object.entries(dos.adjustments).map(([outcome, a]) => [outcome, a ? { label: localize(state, a.label, a.label), amount: a.amount as DegreeAdjustment["amount"] } : a])), predicate: dos.predicate, parentOptions: [], source: weapon.uuid, sourceName: runeName(state, rune), ...testNote(dos.predicate) });
    }
  }
  const damage = calculateStrikeDamage(state, weapon, { proficiencyRank, baseOptions, weaponRollOptions, actionTraits });

  const strikeSource = weapon.rule ? weapon.rule.item : weapon.slug === "basic-unarmed" ? null : weapon;
  return {
    slug: weapon.slug,
    label: weapon.name,
    weapon,
    source: strikeSource?.uuid ?? null,
    sourceName: strikeSource?.name ?? null,
    proficiencyRank,
    domains: attackDomains,
    options: rollOptions,
    baseOptions,
    modifiers: resolved.modifiers,
    totalModifier: resolved.total,
    breakdown: resolved.modifiers.filter((m) => m.enabled).map((m) => `${m.label} ${signed(m.value)}`).join(", "),
    map: maps,
    variants,
    ready,
    visible: !(hiddenCauseStowed || hiddenCauseUnarmed),
    handsAvailable,
    canAttack: true,
    actionTraits,
    weaponTraits: [...weaponTraits].sort(),
    extras,
    damage,
    versatileOptions,
    altUsages: [],
    battleForm: weapon.battleForm,
    overrideKey: `strike:${weapon.slug}${weapon.altUsageType ? `:${weapon.altUsageType}` : ""}`,
    ammunition: strikeAmmunition(state, weapon),
  };
}

/** The property runes on a weapon that the tables describe. */
function propertyRunesOf(weapon: EngineWeapon): WeaponPropertyRuneData[] {
  return weapon.runes.property.map((p) => WEAPON_PROPERTY_RUNES[p]).filter((r): r is WeaponPropertyRuneData => !!r);
}

function runeName(state: EngineState, rune: WeaponPropertyRuneData): string {
  const text = localize(state, rune.name, rune.slug);
  return text.startsWith("PF2E.") ? rune.slug : text;
}

function runeNote(state: EngineState, weapon: EngineWeapon, rune: WeaponPropertyRuneData, note: { outcome?: string[]; predicate?: RollNote["predicate"]; title: string; text: string }, selector: string): RollNote {
  return {
    selector,
    title: localize(state, note.title, runeName(state, rune)),
    text: localize(state, note.text, note.text),
    predicate: note.predicate ?? [],
    parentOptions: [],
    outcome: (note.outcome ?? []) as RollNote["outcome"],
    visibility: null,
    source: weapon.uuid,
    sourceName: runeName(state, rune),
  };
}

/** What ammunition the weapon takes and which stacks in the inventory fit (null when it needs none). */
function strikeAmmunition(state: EngineState, weapon: EngineWeapon): StrikeAmmunition | null {
  const ammo = weapon.system.ammo;
  if (typeof ammo !== "object" || ammo === null || (ammo as { builtIn?: unknown }).builtIn === true) return null;
  const baseType = (ammo as { baseType?: unknown }).baseType;
  return {
    baseType: typeof baseType === "string" ? baseType : null,
    selected: weapon.ammoItem?.inventoryId ?? null,
    compatible: state.inventory.items.filter((a) => ammoMatches(a, weapon)).map((a) => ({ id: a.inventoryId, name: a.displayName, quantity: a.quantity })),
  };
}

/** Foundry's `getPropertyRuneDamage`: the extra dice and modifiers a weapon's property runes add. */
function propertyRuneDamage(state: EngineState, weapon: EngineWeapon, runes: WeaponPropertyRuneData[], options: Set<string>): { dice: DamageDice[]; modifiers: Modifier[] } {
  const dice: DamageDice[] = [];
  const modifiers: Modifier[] = [];
  for (const rune of runes) {
    for (const data of rune.damage?.additional ?? []) {
      const slug = sluggifyRune(data.slug ?? rune.slug);
      if ("modifier" in data) {
        const value = typeof data.modifier === "number" ? data.modifier : resolveRuneFormula(data.modifier, weapon);
        modifiers.push(createModifier({ slug, label: localize(state, data.label, runeName(state, rune)), value, damageType: data.damageType, damageCategory: data.damageCategory ?? null, predicate: data.predicate ?? [], source: weapon.uuid, sourceName: runeName(state, rune) }));
      } else {
        const die = createDamageDice({ selector: "strike-damage", slug, label: runeName(state, rune), diceNumber: data.diceNumber, dieSize: data.dieSize, damageType: data.damageType, category: data.category ?? null, predicate: data.predicate ?? [], critical: data.critical ?? null, source: weapon.uuid, sourceName: runeName(state, rune) });
        testDamageDice(die, options);
        dice.push(die);
      }
    }
  }
  return { dice, modifiers };
}

function sluggifyRune(slug: string): string {
  return slug.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
}

/** A rune's damage formula over the weapon (`@item.baseDamage.dice`). */
function resolveRuneFormula(formula: string, weapon: EngineWeapon): number {
  const view = { baseDamage: weapon.baseDamage, damage: weapon.damage, system: weapon.system, runes: weapon.runes };
  const replaced = replaceFormulaData(formula, (path) => (path.startsWith("item.") ? getProperty(view, path.slice(5)) : undefined));
  try {
    const value = evaluateFormula(replaced.formula);
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

interface DamageParams {
  proficiencyRank: Rank;
  baseOptions: string[];
  weaponRollOptions: Set<string>;
  actionTraits: string[];
}

/** Foundry's WeaponDamagePF2e.calculate, for a success and a critical success. */
export function calculateStrikeDamage(state: EngineState, weapon: EngineWeapon, params: DamageParams): DerivedStrikeDamage | null {
  const synthetics = state.synthetics;
  const domains = getAttackDamageDomains(state, weapon, params.proficiencyRank);
  const options = new Set([...params.baseOptions, ...state.rollOptions.get(domains), ...params.weaponRollOptions, ...params.actionTraits.map((t) => `self:action:trait:${t}`)]);
  const unprocessedBase = weapon.baseDamage;
  if (unprocessedBase.die === null && unprocessedBase.modifier !== 0) unprocessedBase.dice = 0;
  else if (!weapon.dealsDamage) return null;

  const weaponTraits = weapon.traits;
  for (const trait of weaponTraits) options.add(trait);
  const isMelee = weapon.isMelee;
  options.add(isMelee ? "melee" : "ranged");

  const modifiers: Modifier[] = [];
  const damageDice: DamageDice[] = [];
  const notes: RollNote[] = [];

  // Attribute modifier: Strength for melee, propulsive and thrown weapons (halved when propulsive and non-negative).
  const attributeDomains = new Set(["str-damage", "dex-damage", "con-damage", "int-damage", "wis-damage", "cha-damage"]);
  const domain = domains.find((d) => attributeDomains.has(d));
  const strength = state.actor.system.abilities.str.mod;
  if (domain === "str-damage") {
    const value = strength < 0 || !weaponTraits.includes("propulsive") ? strength : Math.floor(strength / 2);
    modifiers.push(createAttributeModifier("str", strength, domains, value, extractModifierAdjustments(synthetics.modifierAdjustments, [...domains, "str-based"], "str")));
  }

  const baseDamage = processBaseDamage(state, weapon, unprocessedBase, domains, options);

  // Splash damage.
  const hasScatter = weaponTraits.some((t) => t.startsWith("scatter-"));
  const splashDamage = weapon.splashDamage || (hasScatter ? weapon.damage.dice || 0 : 0);
  if (splashDamage > 0) {
    const slug = hasScatter ? "scatter" : "splash";
    modifiers.push(createModifier({ slug, label: localize(state, slug === "scatter" ? "PF2E.TraitScatter" : "PF2E.TraitSplash", slug === "scatter" ? "Scatter" : "Splash"), value: splashDamage, damageType: baseDamage.damageType, damageCategory: "splash" }));
  }
  if (weaponTraits.includes("kickback")) modifiers.push(createModifier({ slug: "kickback", label: weaponTraitLabel(state, "kickback"), value: 1 }));

  // Critical specialization effects.
  const critSpecEffect = ((): CritSpecEffect => {
    const critSpecs = synthetics.criticalSpecializations;
    const standard = critSpecs.standard.reduceRight((result: CritSpecEffect | null, cs) => result ?? cs(weapon, options), null);
    const alternate = critSpecs.alternate.reduceRight((result: CritSpecEffect | null, cs) => result ?? cs(weapon, options), null);
    return standard ? (alternate ?? standard) : [];
  })();
  if (critSpecEffect.length > 0) options.add("critical-specialization");
  modifiers.push(...critSpecEffect.filter((e): e is Modifier => "value" in e && !("diceNumber" in e)));
  damageDice.push(...critSpecEffect.filter((e): e is DamageDice => "diceNumber" in e));
  notes.push(...critSpecEffect.filter((e): e is RollNote => "text" in e));

  // Backstabber (needs an off-guard target: recorded, disabled until #19 supplies target options).
  if (weaponTraits.includes("backstabber")) {
    modifiers.push(createModifier({ label: weaponTraitLabel(state, "backstabber"), slug: "backstabber", value: weapon.attackItemBonus > 2 ? 2 : 1, damageCategory: "precision", predicate: ["target:condition:off-guard"] }));
  }

  // Striking synthetics (ABP, #12) may raise the weapon's dice.
  const strikingSynthetic = domains.flatMap((key) => synthetics.striking[key] ?? []).filter((wp) => testPredicate(wp.predicate, options)).reduce((highest: { bonus: number } | null, current) => (highest && highest.bonus > current.bonus ? highest : current), null);
  if (strikingSynthetic && baseDamage.die) {
    weapon.system.damage!.dice = baseDamage.dice = Math.max(weapon.system.damage!.dice ?? 1, strikingSynthetic.bonus + 1);
    weapon.system.runes!.striking = Math.max(weapon.runes.striking, strikingSynthetic.bonus);
  }
  const strikingDice = (weapon.system.damage!.dice ?? 1) - weapon.inherentDice;

  // Deadly, fatal, forceful, tearing, twin, venomous.
  for (const slug of weaponTraits.filter((t) => t.startsWith("deadly-"))) {
    const baseNumber = Number(/-(\d)d\d{1,2}$/.exec(slug)?.[1]) || 1;
    const diceNumber = strikingDice > 1 ? strikingDice * baseNumber : baseNumber;
    const dieSize = /-\d?(d\d{1,2})$/.exec(slug)?.[1];
    damageDice.push(createDamageDice({ selector: `${weapon.id}-damage`, slug, label: weaponTraitLabel(state, slug), diceNumber, dieSize: isDamageDieSize(dieSize) ? dieSize : baseDamage.die, critical: true }));
  }
  for (const trait of weaponTraits.filter((t) => t.startsWith("fatal-d"))) {
    const dieSize = trait.substring(trait.indexOf("-") + 1);
    if (!isDamageDieSize(dieSize)) continue;
    damageDice.push(createDamageDice({ selector: `${weapon.id}-damage`, slug: trait, label: weaponTraitLabel(state, trait), diceNumber: 1, dieSize, critical: true, enabled: true, override: { dieSize } }));
  }
  if (weaponTraits.includes("forceful")) {
    modifiers.push(
      createModifier({ slug: "forceful-second", label: localize(state, "PF2E.Item.Weapon.Forceful.Second", "Forceful 2nd Attack"), value: weapon.inherentDice + strikingDice, type: "circumstance", ignored: true }),
      createModifier({ slug: "forceful-third", label: localize(state, "PF2E.Item.Weapon.Forceful.Third", "Forceful 3rd Attack"), value: 2 * (weapon.inherentDice + strikingDice), type: "circumstance", ignored: true }),
    );
  }
  if (weaponTraits.includes("tearing")) modifiers.push(createModifier({ label: weaponTraitLabel(state, "tearing"), slug: "tearing", value: strikingDice > 1 ? 2 : 1, damageType: "bleed", damageCategory: "persistent" }));
  if (weaponTraits.includes("twin")) modifiers.push(createModifier({ slug: "twin-second", label: localize(state, "PF2E.Item.Weapon.Twin.SecondPlus", "Twin 2nd+ Attack"), value: weapon.inherentDice + strikingDice, type: "circumstance", ignored: true }));
  if (weaponTraits.includes("venomous")) modifiers.push(createModifier({ label: weaponTraitLabel(state, "venomous"), slug: "venomous", value: strikingDice > 1 ? 2 : 1, damageType: "poison", damageCategory: "persistent" }));

  // Property runes: extra damage, notes, modifier adjustments (grievous doubles a pick's critical specialization) and ignored resistances.
  const runes = propertyRunesOf(weapon);
  const runeDamage = propertyRuneDamage(state, weapon, runes, options);
  damageDice.push(...runeDamage.dice);
  modifiers.push(...runeDamage.modifiers);
  for (const rune of runes) for (const note of rune.damage?.notes ?? []) notes.push(runeNote(state, weapon, rune, note, "strike-damage"));
  const runeAdjustments = runes.flatMap((rune) => (rune.damage?.adjustments ?? []).map((a) => ({ slug: a.slug, test: (opts: Set<string> | string[]) => testPredicate(a.predicate, new Set(opts)), getNewValue: a.multiply !== undefined ? (current: number) => current * a.multiply! : undefined, sourceName: runeName(state, rune) })));
  const ignoredResistances = runes.flatMap((rune) => rune.damage?.ignoredResistances ?? []).map((r) => ({ ...r }));

  // Precious materials that change how damage is resisted.
  const material = weapon.material.type;
  const materials = new Set<string>(material && (MATERIAL_DAMAGE_EFFECTS as readonly string[]).includes(material) ? [material] : []);
  for (const adjustment of synthetics.strikeAdjustments) adjustment.adjustDamageRoll?.(weapon, { materials });
  for (const m of materials) options.add(`item:material:${m}`);

  const baseUncategorized: BaseDamageData | null = ((): BaseDamageData | null => {
    const diceNumber = baseDamage.die ? baseDamage.dice : 0;
    return diceNumber > 0 || baseDamage.modifier !== 0
      ? { diceNumber, dieSize: baseDamage.die, modifier: baseDamage.modifier, damageType: baseDamage.damageType, category: null, materials: [...materials] }
      : null;
  })();
  const basePersistent: BaseDamageData | null = baseDamage.persistent?.faces
    ? { diceNumber: baseDamage.persistent.number, dieSize: `d${baseDamage.persistent.faces}` as DamageDieSize, damageType: baseDamage.persistent.type, category: "persistent" }
    : baseDamage.persistent?.number
      ? { modifier: baseDamage.persistent.number, damageType: baseDamage.persistent.type, category: "persistent" }
      : null;
  if (!(baseUncategorized || basePersistent || splashDamage)) return null;
  const base = [baseUncategorized, basePersistent].filter((b): b is BaseDamageData => !!b);

  for (const modifier of modifiers) {
    modifier.domains = [...domains];
    if (modifier.adjustments.length === 0) modifier.adjustments = extractModifierAdjustments(synthetics.modifierAdjustments, domains, modifier.slug);
    modifier.adjustments.push(...runeAdjustments.filter((a) => a.slug === modifier.slug).map((a) => (a.getNewValue ? { slug: a.slug, test: a.test, getNewValue: a.getNewValue, sourceName: a.sourceName } : { slug: a.slug, test: a.test, sourceName: a.sourceName })));
    modifier.alterations = extractDamageAlterations(synthetics.damageAlterations, domains, modifier.slug);
  }
  for (const dice of damageDice) dice.alterations = extractDamageAlterations(synthetics.damageAlterations, domains, dice.slug);

  const extractOptions = { selectors: domains, test: options, resolvables: { weapon }, injectables: { weapon } };
  const extracted = processDamageCategoryStacking(base, {
    modifiers: [...modifiers, ...extractModifiers(synthetics, domains, extractOptions)],
    dice: extractDamageDice(synthetics, extractOptions),
    test: options,
  });
  const testedModifiers = extracted.modifiers;
  damageDice.push(...extracted.dice);

  const facesUpgraded = { value: weapon.damageFacesUpgraded };
  for (const dice of damageDice) for (const alteration of dice.alterations) applyDamageAlteration(alteration, dice, { item: weapon, test: options, facesUpgraded });
  for (const modifier of testedModifiers) for (const alteration of modifier.alterations) applyDamageAlteration(alteration, modifier, { item: weapon, test: options, facesUpgraded });
  const maxIncreases = facesUpgraded.value ? 0 : 1;

  let finalDice = damageDice;
  let finalModifiers = testedModifiers;
  if (!(base[0]!.diceNumber || base[0]!.modifier)) {
    finalDice = finalDice.filter((d) => ![null, "precision"].includes(d.category));
    finalModifiers = finalModifiers.filter((m) => ![null, "precision"].includes(m.damageCategory));
  }

  // Rule Elements that exclude damage parts (a battle form), then the final predicate test.
  const notIgnored = [...finalModifiers, ...finalDice].filter((m) => !m.ignored);
  for (const rule of state.rules) if (!rule.ignored) rule.handler?.applyDamageExclusion?.(rule, state, weapon, notIgnored);
  for (const part of notIgnored) {
    if ("diceNumber" in part) testDamageDice(part, new Set([...options, ...damageDiceRollOptions(part)]));
    else part.ignored = !testPredicate(part.predicate, new Set([...options, ...part.parentOptions]));
  }

  const labels: FormulaLabels = {
    damageType: (type) => damageTypeLabel(state, type),
    persistent: (type) => localize(state, "PF2E.Damage.PersistentTooltip", "Persistent {damageType}", { damageType: damageTypeLabel(state, type) }),
    splash: localize(state, "PF2E.TraitSplash", "Splash"),
  };
  const formulaData = { base, dice: finalDice, modifiers: finalModifiers, maxIncreases };
  const computed: Record<Degree, ReturnType<typeof createDamageFormula>> = {
    failure: createDamageFormula(formulaData, "failure", labels),
    success: createDamageFormula(formulaData, "success", labels),
    criticalSuccess: createDamageFormula(formulaData, "criticalSuccess", labels),
  };
  const damageNotes = extractNotes(synthetics.rollNotes, domains).map((n) => ({ ...n, enabled: n.predicate.length === 0 || testPredicate(n.predicate, new Set([...options, ...n.parentOptions])), postRoll: false }));
  return {
    domains,
    options,
    base,
    dice: finalDice,
    modifiers: finalModifiers,
    materials: [...materials],
    formula: { failure: computed.failure?.formula ?? null, success: computed.success?.formula ?? null, criticalSuccess: computed.criticalSuccess?.formula ?? null },
    breakdown: { failure: computed.failure?.breakdown ?? [], success: computed.success?.breakdown ?? [], criticalSuccess: computed.criticalSuccess?.breakdown ?? [] },
    notes: [...notes.map((n) => ({ ...n, enabled: true, postRoll: false })), ...damageNotes],
    ephemeralEffects: extractEphemeralEffects(synthetics, domains).map((e) => ({ ...e, enabled: e.test(options) })),
    criticalSpecialization: critSpecEffect.length > 0,
    ignoredResistances,
  };
}

/** Foundry's processBaseDamage: apply `base`-slugged damage alterations to the weapon's own damage. */
function processBaseDamage(state: EngineState, weapon: EngineWeapon, unprocessed: WeaponDamageData, domains: string[], options: Set<string>): WeaponDamageData {
  const alterations = extractDamageAlterations(state.synthetics.damageAlterations, domains, "base");
  if (alterations.length === 0) return { ...unprocessed };
  const dice = unprocessed.dice > 0 ? createDamageDice({ selector: "strike-damage", slug: "base", damageType: unprocessed.damageType, diceNumber: unprocessed.dice, dieSize: unprocessed.die }) : null;
  const modifier = unprocessed.modifier !== 0 ? createModifier({ slug: "base", label: "Base", damageType: unprocessed.damageType, value: unprocessed.modifier }) : null;
  const persistent = unprocessed.persistent
    ? unprocessed.persistent.faces
      ? createDamageDice({ selector: "strike-damage", slug: "base-persistent", category: "persistent", damageType: unprocessed.persistent.type, diceNumber: unprocessed.persistent.number, dieSize: `d${unprocessed.persistent.faces}` as DamageDieSize })
      : createModifier({ slug: "base-persistent", label: "Base", damageCategory: "persistent", damageType: unprocessed.persistent.type, value: unprocessed.persistent.number })
    : null;
  const facesUpgraded = { value: weapon.damageFacesUpgraded };
  for (const alteration of alterations) {
    if (dice) applyDamageAlteration(alteration, dice, { item: weapon, test: options, facesUpgraded });
    if (modifier) applyDamageAlteration(alteration, modifier, { item: weapon, test: options, facesUpgraded });
    if (persistent) applyDamageAlteration(alteration, persistent, { item: weapon, test: options, facesUpgraded });
  }
  weapon.damageFacesUpgraded = facesUpgraded.value;
  return {
    damageType: dice?.damageType ?? modifier?.damageType ?? unprocessed.damageType,
    dice: dice?.diceNumber ?? 0,
    die: dice?.dieSize ?? null,
    modifier: modifier?.value ?? 0,
    persistent: persistent ? ("diceNumber" in persistent ? { number: persistent.diceNumber, faces: Number(persistent.dieSize?.replace("d", "")) || null, type: persistent.damageType ?? "untyped" } : { number: persistent.value, faces: null, type: persistent.damageType ?? "untyped" }) : null,
  };
}
